import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { createPortal } from 'react-dom';
import axios from 'axios';
import Dashboard from './Dashboard';
import {
 Check,
 Loader2,
 Rocket,
 Building2,
 Image as ImageIcon,
 UtensilsCrossed,
 ArrowRight,
 ExternalLink,
 Sparkles,
 AlertCircle,
 Clock,
 Layers,
 CreditCard,
 Eye,
 Plus,
 MessageCircle,
 Lock,
 X,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import OnboardingShell from '../components/OnboardingShell';
import { notifyReadinessChanged } from '../utils/readinessBus';
import useDebouncedSave from '../hooks/useDebouncedSave';
import SaveStatusPill from '../components/SaveStatusPill';
import MoneyInput from '../components/MoneyInput';
import CoverPhotoPreview from '../components/CoverPhotoPreview';
import RestaurantStatusControl from '../components/RestaurantStatusControl';
import HolidayHoursManager from '../components/HolidayHoursManager';
import OrderChannelsForm from '../components/OrderChannelsForm';
import OrderFulfillmentForm from '../components/OrderFulfillmentForm';
import CuisineTypeSelector from '../components/CuisineTypeSelector';
import FastByNoFeeMarketing from '../components/FastByNoFeeMarketing';
import ImageCropperModal from '../components/ImageCropperModal';
import { US_STATES } from '../utils/usStates';
import { DELIVERY_RADIUS_OPTIONS, DEFAULT_DELIVERY_RADIUS } from '../utils/deliveryRadius';
import useZipLookup from '../hooks/useZipLookup';

const API = process.env.REACT_APP_BACKEND_URL;

/* --- helpers ---------------------------------------------------------- */

const compressImage = (file, max = 1200) =>
 new Promise((resolve, reject) => {
 const reader = new FileReader();
 reader.onload = (ev) => {
 const img = new Image();
 img.onload = () => {
 let { width, height } = img;
 if (width > max || height > max) {
 if (width > height) {
 height = Math.round(height * (max / width));
 width = max;
 } else {
 width = Math.round(width * (max / height));
 height = max;
 }
 }
 const canvas = document.createElement('canvas');
 canvas.width = width;
 canvas.height = height;
 const ctx = canvas.getContext('2d');
 ctx.fillStyle = '#ffffff';
 ctx.fillRect(0, 0, width, height);
 ctx.drawImage(img, 0, 0, width, height);
 const mime =
 file.type === 'image/png'
? 'image/jpeg'
: file.type === 'image/webp'
? 'image/webp'
: 'image/jpeg';
 resolve(canvas.toDataURL(mime, 0.86));
 };
 img.onerror = reject;
 img.src = ev.target.result;
 };
 reader.onerror = reject;
 reader.readAsDataURL(file);
 });

const CUISINES = [
 'American', 'Italian', 'Mexican', 'Chinese', 'Japanese', 'Thai', 'Indian',
 'Mediterranean', 'Greek', 'French', 'Vietnamese', 'Korean', 'Brazilian',
 'Caribbean', 'Middle Eastern', 'Pizza', 'Burgers', 'Sushi', 'Seafood',
 'BBQ', 'Bakery', 'Cafe', 'Vegan', 'Healthy',
];

/* --- subcomponents ---------------------------------------------------- */

const SectionShell = ({ idx, title, subtitle, ok, icon: Icon, children, testid }) => (
 <section
 data-testid={testid}
 className={`rounded-3xl border bg-white/[0.02] p-6 sm:p-8 transition-all ${
 ok? 'border-emerald-500/30 bg-emerald-500/[0.02]': 'border-white/8'
 }`}
 >
 <header className="flex items-start gap-4 mb-6">
 <div
 className={`w-11 h-11 rounded-xl border flex items-center justify-center shrink-0 ${
 ok
? 'bg-emerald-500/15 border-emerald-500/50 text-emerald-300'
: 'bg-yellow-500/[0.10] border-yellow-500/30 text-yellow-300'
 }`}
 >
 {ok? <Check className="w-5 h-5" strokeWidth={2.4} />: <Icon className="w-5 h-5" strokeWidth={1.7} />}
 </div>
 <div className="flex-1 min-w-0">
 <div className="flex items-center gap-2 mb-1">
 <span className="text-[10.5px] uppercase tracking-[0.18em] text-white/40 font-bold tabular-nums">
 Section {idx}
 </span>
 {ok && (
 <span
 data-testid={`${testid}-done-pill`}
 className="text-[10px] uppercase tracking-[0.18em] text-emerald-300 font-bold"
 >
 Complete
 </span>
 )}
 </div>
 <h3 className="font-['Outfit',_Inter,_sans-serif] text-[20px] sm:text-[22px] font-medium tracking-tight text-white">
 {title}
 </h3>
 <p className="mt-1 text-[13px] text-white/55 leading-relaxed">{subtitle}</p>
 </div>
 </header>
 {children}
 </section>
);

/* ---------------------------------------------------------------------
 * Publish-checklist field targets — each checklist key maps to:
 * - field: the EXACT data-testid of the input/control the owner must fix.
 * If `field` is a function, it is called at click-time with the
 * current `sections` object so the helper can pick the right
 * field dynamically (e.g. the first non-closed day for hours).
 * - label: short imperative label for the "Go to..." button copy.
 * Each entry's `field` MUST resolve to an actual <input>, <button> or
 * focusable control — never the parent section. Scrolling to a section
 * dropped the user mid-form; this map fixes that by going field-direct.
 * ------------------------------------------------------------------- */
const CHECKLIST_TARGETS = {
 name: { field: 'setup-input-name', label: 'Restaurant Name' },
 phone: { field: 'setup-input-phone', label: 'Phone Number' },
 address: { field: 'setup-input-address', label: 'Address' },
 cuisine_type: { field: 'setup-cuisine-types-options', label: 'Cuisine Type' },
 logo: { field: 'setup-logo-upload-btn', label: 'Restaurant Logo' },
 hours: { field: 'setup-hours-open-mon', label: 'Business Hours' },
 categories: { field: 'setup-input-category-name', label: 'Menu Categories' },
 items: { field: 'setup-input-item-name', label: 'Menu Items' },
 order_channels: { field: 'channel-toggle-whatsapp', label: 'Order Channels' },
 fulfillment: { field: 'fulfillment-pickup-toggle', label: 'Pickup / Delivery' },
};

/* Scrolls DIRECTLY to the exact field that needs the owner's attention,
 * focuses it, and paints a temporary gold pulse so it's immediately visible.
 * Falls back to the parent <section> if the field can't be located (e.g.
 * because the form is conditionally rendered) so the owner is never stranded.
 */
const focusChecklistItem = (key) => {
 const target = CHECKLIST_TARGETS[key];
 if (!target || typeof document === 'undefined') return;

 // Prefer the exact field. If it's not in the DOM (e.g. the section isn't
 // expanded yet, or the form is lazy-rendered), walk up the corresponding
 // SectionShell as a graceful fallback so the user lands SOMEWHERE useful.
 const fieldEl =
 document.querySelector(`[data-testid="${target.field}"]`) ||
 // Order-channels and fulfillment widgets nest their own roots — find
 // the closest setup-section-* if the specific control is missing.
 document.querySelector(`[data-testid^="setup-section-"][data-testid*="${key.split('_')[0]}"]`) ||
 null;
 if (!fieldEl) return;

 // Smooth scroll directly to the field, centered in the viewport. This is
 // the single source of truth for "where the page lands" — no parent-
 // section pre-scroll that could leave the input above the fold.
 fieldEl.scrollIntoView({ behavior: 'smooth', block: 'center' });

 // After the smooth scroll has started (350ms is enough for the animation
 // to kick in but short enough that the highlight is visible during motion),
 // focus the input and paint the pulse.
 setTimeout(() => {
 try { fieldEl.focus({ preventScroll: true }); } catch (_) { /* not focusable, that's fine */ }
 fieldEl.classList.add('nf-focus-highlight');
 setTimeout(() => fieldEl.classList.remove('nf-focus-highlight'), 2400);
 }, 350);
};

const Field = ({ label, children, hint }) => (
 <label className="block">
 <span className="block text-[11px] uppercase tracking-[0.16em] text-white/55 font-bold mb-1.5">
 {label}
 </span>
 {children}
 {hint && <span className="block mt-1 text-[11.5px] text-white/40">{hint}</span>}
 </label>
);

const inputCls =
 'w-full px-4 py-3 rounded-xl bg-black/40 border border-white/12 focus:border-yellow-500/60 focus:bg-black/60 text-white placeholder:text-white/30 outline-none transition-colors text-[14.5px]';

/* --- main page -------------------------------------------------------- */

const OnboardingSetupPage = () => {
 const navigate = useNavigate();
 const { token } = useAuth();

 const [loading, setLoading] = useState(true);
 const [restaurant, setRestaurant] = useState(null);
 const [readiness, setReadiness] = useState(null);
 const [error, setError] = useState('');
 // iter132 — when the publish click hits a subscription gate (HTTP 402),
 // we surface a dedicated inline activation card with a one-click button
 // that takes the owner straight to Stripe Checkout (NOT to the
 // /complete-subscription page, which owners reported as a confusing
 // "blank screen"). When `needsTrialActivation` is true the card replaces
 // the generic error banner below the publish CTA.
 const [needsTrialActivation, setNeedsTrialActivation] = useState(false);
 const [activatingTrial, setActivatingTrial] = useState(false);
 // iter132 — Stripe Checkout success_url now returns to this page with
 // `?activated=1&session_id=...`. When detected, we finalise the session
 // server-side, refresh the restaurant doc, then auto-fire the publish
 // flow so the owner lands on the 🎉 Grow My Restaurant popup WITHOUT the
 // legacy /onboarding/subscription-success interstitial. The ref guards
 // against double-firing under React.StrictMode + hot reloads.
 const [searchParams, setSearchParams] = useSearchParams();
 const trialAutoPublishFiredRef = useRef(false);
 const [finalizingTrial, setFinalizingTrial] = useState(
   searchParams.get('activated') === '1' && !!searchParams.get('session_id')
 );

 // iter115 — Step 3 (this page) is the "Go live" restaurant setup. It must
 // be VIEWABLE before Step 2 (Stripe Connect) is complete so the owner can
 // preview what they'll have to fill, but every interactive action is
 // hard-blocked until Stripe is connected. Otherwise the restaurant could
 // be fully set up + published without ever activating the subscription.
 // Source of truth: the restaurant doc returned by GET /api/restaurants/:id
 // already exposes `stripe_account_id` + `stripe_charges_enabled` after the
 // /api/restaurants/:id/stripe/refresh-status webhook runs.
 //
 // iter132 — restaurants that are ALREADY LIVE bypass this lock entirely.
 // They reached "live" status via a prior path (legacy onboarding or
 // backfilled data) so re-locking them now would trap them on
 // /onboarding/setup with no way to re-trigger the celebration popup.
 const stripeReady = useMemo(() => {
 const r = restaurant || {};
 if (r.is_active && r.go_live_at) return true;
 return!!(r.stripe_account_id && r.stripe_charges_enabled);
 }, [restaurant]);

 // Single source of truth for the lock messaging. Used by the top banner,
 // the click-shield toast, and any inline lock pills we render around
 // sensitive actions. iter123 — distinguishes between "no account yet"
 // and "account created but Stripe still pending".
 const lockMessage =!token
? 'Complete Step 1 (Create your account) first to unlock your restaurant setup.'
: 'Complete Step 2 (Connect Stripe) first to unlock your restaurant setup.';
 const onLockedClick = () => {
 toast.error(lockMessage, {
 description:!token
? 'Your restaurant account is needed before you can set up hours, menu and Go live.'
: 'Pickup, delivery, photos, menu and Go live all unlock once Stripe payouts are active.',
 action: {
 label:!token? 'Go to Step 1': 'Go to Step 2',
 onClick: () => navigate(!token? '/onboarding/account': '/onboarding/stripe-connect'),
 },
 });
 };

 // ZIP-based City/State autofill. The hook caches per-session results
 // and surfaces a `warning` string under the inputs when ZIP and State
 // disagree (server-side validator also blocks the save).
 const zipLookup = useZipLookup(token);

 // Section A — profile (extended 2026-06-03: now also handles cover
 // image, delivery fee, minimum order, the toggle, and the
 // multi-cuisine selector so the onboarding parity with Settings is
 // complete).
 const [profile, setProfile] = useState({
 name: '',
 phone: '',
 address: '',
 city: '',
 state: '',
 zip_code: '',
 cuisine_type: '',
 cuisine_types: [],
 description: '',
 cover_url: '',
 delivery_fee: 0,
 min_order: 0,
 delivery_radius_miles: DEFAULT_DELIVERY_RADIUS,
 manual_status: 'open',
 });
 const [savingProfile, setSavingProfile] = useState(false);
 const [savingCover, setSavingCover] = useState(false);
 const fileCoverRef = useRef(null);

 // Section B — logo
 const [logoUrl, setLogoUrl] = useState('');
 const [savingLogo, setSavingLogo] = useState(false);

 // Section — business hours (day → {open, close, closed})
 const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
 const DAY_LABELS = { mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday', fri: 'Friday', sat: 'Saturday', sun: 'Sunday' };
 const [hours, setHours] = useState({});
 const [savingHours, setSavingHours] = useState(false);

 // Section — categories
 const [categories, setCategories] = useState([]);
 const [newCategory, setNewCategory] = useState('');
 const [savingCategory, setSavingCategory] = useState(false);

 // Section C — first menu item
 const [item, setItem] = useState({ name: '', description: '', price: '', image_url: '' });
 const [savingItem, setSavingItem] = useState(false);
 // Inline error shown next to the "Add to menu" button — separate from
 // the global `error` banner at the top of the page so owners scrolled
 // down to Section 5 can actually see the feedback without scrolling.
 const [itemError, setItemError] = useState('');
 // Tracks which checklist task the owner just navigated to via the
 // "Go to..." buttons. When the corresponding readiness signal flips
 // to ok, an effect below auto-scrolls the page back down to the
 // publish checklist so the owner doesn't have to manually scroll
 // through the form on every fix → tick cycle.
 const [pendingChecklistKey, setPendingChecklistKey] = useState(null);

 // ── Cropper modal state ─────────────────────────────────────────────
 // Same Level-2 UX as the dashboard: auto-mode default crop, owner can
 // drag/zoom before saving. Each file-picker stages the pre-compressed
 // data URI into `cropperSrc`; the cropper emits the final cropped
 // JPEG to a per-target persist handler.
 const [cropperSrc, setCropperSrc] = useState(null);
 const [cropperTarget, setCropperTarget] = useState(null); // 'logo' | 'cover' | 'item'
 const CROP_ASPECT = { logo: 1, cover: 16 / 9, item: 4 / 3 };
 const CROP_TITLE = {
 logo: 'Adjust your restaurant logo',
 cover: 'Adjust your restaurant main photo',
 item: 'Adjust your food photo',
 };
 const closeCropper = () => { setCropperSrc(null); setCropperTarget(null); };

 // Final go-live state
 const [goingLive, setGoingLive] = useState(false);
 const [liveResult, setLiveResult] = useState(null);
 // Controls the "Almost Ready!" modal that appears when the owner clicks
 // Publish while requirements are still missing. Pure UI state — no impact
 // on backend publish validation.
 // iter132 — the "Almost Ready!" modal was removed (it confused owners).
 // We no longer need a setShowAlmostReady state; we redirect the user
 // straight to the missing field via focusChecklistItem + a toast.

 const fileLogoRef = useRef(null);
 const fileItemRef = useRef(null);

 useEffect(() => {
 if (!token) {
 // iter123 — Do NOT redirect anonymous visitors away. They are
 // allowed to PREVIEW Step 4 in a locked state (a la iter115's
 // pattern). The existing `stripeReady=false` lock banner +
 // click-shield naturally activates because we never fetch a
 // restaurant doc, so `stripe_charges_enabled` stays false.
 // We swap the banner copy below to point them to Step 1 instead
 // of Step 2 when there's no token at all.
 setLoading(false);
 return;
 }
 (async () => {
 try {
 const stateRes = await axios.get(`${API}/api/onboarding/state`, {
 headers: { Authorization: `Bearer ${token}` },
 });
 const rid = stateRes.data.restaurant_id;
 const [rRes, readyRes, catsRes] = await Promise.all([
 axios.get(`${API}/api/restaurants/${rid}`),
 axios.get(`${API}/api/onboarding/readiness`, {
 headers: { Authorization: `Bearer ${token}` },
 }),
 axios.get(`${API}/api/restaurants/${rid}/categories`).catch(() => ({ data: [] })),
 ]);
 const r = rRes.data.restaurant || {};
 setRestaurant(r);
 setReadiness(readyRes.data);
 setProfile({
 name: r.name || '',
 phone: r.phone || '',
 address: r.address || '',
 city: r.city || '',
 state: r.state || '',
 zip_code: r.zip_code || '',
 delivery_radius_miles: r.delivery_radius_miles?? DEFAULT_DELIVERY_RADIUS,
 cuisine_type: r.cuisine_type || '',
 // Hydrate the multi-cuisine array. Falls back to splitting the
 // legacy comma-separated `cuisine_type` string if no array exists.
 cuisine_types: (
 Array.isArray(r.cuisine_types) && r.cuisine_types.length
? r.cuisine_types
: (r.cuisine_type
? String(r.cuisine_type).split(',').map((s) => s.trim()).filter(Boolean)
: [])
 ),
 description: r.description || '',
 cover_url: r.cover_url || '',
 delivery_fee: r.delivery_fee || 0,
 min_order: r.min_order || 0,
 manual_status: r.manual_status === 'busy'? 'busy': 'open',
 manual_status_until: r.manual_status_until || null,
 });
 setLogoUrl(r.logo_url || '');
 // Seed business-hours editor — accept either {open,close,closed?} or older shape
 const bh = r.business_hours || {};
 const seeded = {};
 DAY_KEYS.forEach((d) => {
 const v = bh[d] || {};
 seeded[d] = {
 closed: v.closed === true,
 open: v.open || '11:00',
 close: v.close || '22:00',
 };
 });
 setHours(seeded);
 setCategories(catsRes.data || []);
 } catch (e) {
 setError(e?.response?.data?.detail || 'Could not load your setup status.');
 } finally {
 setLoading(false);
 }
 })();
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [token, navigate]);

 const refreshReadiness = async () => {
 const r = await axios.get(`${API}/api/onboarding/readiness`, {
 headers: { Authorization: `Bearer ${token}` },
 });
 setReadiness(r.data);
 // Broadcast so any other page (Dashboard / Hub) watching the readiness
 // endpoint re-validates its checklist on next visit / tab focus.
 notifyReadinessChanged();
 };

 // iter142 — Menu Manager modal state. Instead of navigating the owner
 // to /dashboard (which would eject them from the wizard and expose
 // Overview / Orders / Insights / Payments before they've finished
 // setup), we open the SAME Dashboard component inside a portal with
 // `embedded={true}`. That prop hides the sidebar / hero / bell prompt /
 // welcome flow and locks the tab to "menu" so the owner sees only the
 // Menu Manager. On close we re-fetch readiness so the "Menu items" +
 // "Categories" checklist rows flip to ✓ automatically.
 const [menuManagerOpen, setMenuManagerOpen] = useState(false);
 const openMenuManager = () => setMenuManagerOpen(true);
 const closeMenuManager = async () => {
   setMenuManagerOpen(false);
   try { await refreshReadiness(); } catch { /* soft-fail */ }
 };

 // iter136 — Auto-redirect already-live restaurants to their dashboard.
 // When an owner whose restaurant has already gone live (is_active +
 // go_live_at) navigates back to /onboarding/setup they used to stay
 // trapped on the "Welcome to NoFee — let's set up your restaurant"
 // wizard with SETUP · 100% COMPLETE — but with none of the dashboard
 // tools (restaurant info, payouts, Stripe, notifications) visible.
 // Now they land on /dashboard automatically. Two edge cases are
 // explicitly preserved on this page:
 //   1. The Stripe trial finalisation return (?activated=1&session_id=…)
 //      — we still hit /api/onboarding/go-live and show the 🎉 Grow My
 //      Restaurant celebration popup before the owner navigates away.
 //   2. The just-published state (liveResult is set) — the celebration
 //      popup is currently on screen and MUST NOT be pre-empted.
 useEffect(() => {
   if (loading) return;
   if (!restaurant) return;
   const isLive = !!(restaurant.is_active && restaurant.go_live_at);
   if (!isLive) return;
   const finalizingActivation = searchParams.get('activated') === '1';
   if (finalizingActivation) return;
   // iter143 — Stepper clicks pass `?edit=1` to signal that the owner
   // explicitly wants to review / edit their setup. Skip the redirect
   // so they can actually reach the wizard form instead of being
   // ping-ponged back to /dashboard.
   if (searchParams.get('edit') === '1') return;
   if (liveResult) return;
   navigate('/dashboard', { replace: true });
 }, [loading, restaurant?.is_active, restaurant?.go_live_at, liveResult, searchParams]);

 // iter141 — Trial-first guard. The onboarding sequence now runs
 //   Account → Plan → Stripe → **Trial** → Setup → Publish → Dashboard
 // so an owner who lands on /onboarding/setup WITHOUT an active trial
 // is out of order (deep-linked, back-button, resume-from-email, etc.).
 // Bounce them back to /onboarding/trial so the trial gets activated
 // BEFORE they invest time filling in the setup form. Exceptions:
 //   • Already-live restaurants — handled by the effect above.
 //   • Stripe checkout return (?activated=1) — we're finalising the
 //     trial right now; leaving mid-flow would trap the owner.
 //   • Stepper explicit edit (?edit=1) — the owner opened the wizard
 //     from the stepper on purpose to review a completed step; the
 //     trial guard should not preempt that.
 useEffect(() => {
   if (loading) return;
   if (!restaurant) return;
   if (restaurant.is_active && restaurant.go_live_at) return; // handled elsewhere
   if (searchParams.get('activated') === '1') return; // finalising trial
   if (searchParams.get('edit') === '1') return; // stepper edit intent
   const status = String(restaurant.subscription_status || '').toLowerCase();
   const trialOk = status === 'trialing' || status === 'active';
   if (trialOk) return;
   navigate('/onboarding/trial', { replace: true });
 }, [loading, restaurant?.subscription_status, restaurant?.is_active, restaurant?.go_live_at, searchParams]);

 // ── Auto-save (2026-06-09): without this, owners would type phone /
 // address / cuisine, scroll down to the checklist, and see ❌ REQUIRED
 // still because the data never left local state — they had to find the
 // small "Save info" button at the end of the form first. Now every
 // edit syncs to the backend after 700 ms of stillness and triggers a
 // readiness refresh, so the checklist updates in-place. The explicit
 // Save buttons remain as a safety net + status indicator.
 const { status: profileSaveStatus, error: profileSaveError } = useDebouncedSave(
 profile,
 async (v) => {
 if (!restaurant?.id) return;
 // Strip legacy `cuisine_type` — backend mirrors from `cuisine_types`.
 const { cuisine_type: _legacy,...payload } = v;
 void _legacy;
 await axios.put(`${API}/api/restaurants/${restaurant.id}`, payload, {
 headers: { Authorization: `Bearer ${token}` },
 });
 await refreshReadiness();
 },
 { delay: 700, enabled:!!restaurant?.id &&!zipLookup.warning },
 );

 const { status: hoursSaveStatus, error: hoursSaveError } = useDebouncedSave(
 hours,
 async (v) => {
 if (!restaurant?.id) return;
 const payload = {};
 DAY_KEYS.forEach((d) => {
 const h = v[d] || {};
 payload[d] = h.closed
? { closed: true, open: '', close: '' }
: { open: h.open || '11:00', close: h.close || '22:00' };
 });
 await axios.post(
 `${API}/api/restaurants/${restaurant.id}/business-hours/confirm`,
 { business_hours: payload },
 { headers: { Authorization: `Bearer ${token}` } },
 );
 await refreshReadiness();
 },
 { delay: 700, enabled:!!restaurant?.id && Object.keys(hours).length > 0 },
 );

 const refreshCategories = async () => {
 if (!restaurant?.id) return;
 const r = await axios.get(`${API}/api/restaurants/${restaurant.id}/categories`);
 setCategories(r.data || []);
 };

 const onSaveHours = async () => {
 if (!restaurant) return;
 setSavingHours(true);
 setError('');
 try {
 // Strip the `closed` flag back into the {open,close} shape — when closed,
 // open==close==null tells the public menu to render "Closed".
 const payload = {};
 DAY_KEYS.forEach((d) => {
 const h = hours[d] || {};
 payload[d] = h.closed
? { closed: true, open: '', close: '' }
: { open: h.open || '11:00', close: h.close || '22:00' };
 });
 await axios.post(
 `${API}/api/restaurants/${restaurant.id}/business-hours/confirm`,
 { business_hours: payload },
 { headers: { Authorization: `Bearer ${token}` } },
 );
 await refreshReadiness();
 } catch (err) {
 setError(err?.response?.data?.detail || 'Could not save business hours.');
 } finally {
 setSavingHours(false);
 }
 };

 const onAddCategory = async (e) => {
 e?.preventDefault?.();
 const name = (newCategory || '').trim();
 if (!name ||!restaurant) return;
 setSavingCategory(true);
 setError('');
 try {
 await axios.post(
 `${API}/api/restaurants/${restaurant.id}/categories`,
 { name, order: categories.length, is_active: true },
 { headers: { Authorization: `Bearer ${token}` } },
 );
 setNewCategory('');
 await Promise.all([refreshCategories(), refreshReadiness()]);
 } catch (err) {
 setError(err?.response?.data?.detail || 'Could not create the category.');
 } finally {
 setSavingCategory(false);
 }
 };

 const onSaveProfile = async (e) => {
 e?.preventDefault?.();
 if (!restaurant) return;
 setSavingProfile(true);
 setError('');
 try {
 // The backend mirrors `cuisine_types` ↔ `cuisine_type`, so we just
 // send the array; the server will keep the legacy string in sync.
 const { cuisine_type: _legacy,...payload } = profile;
 void _legacy; // explicitly strip so we don't accidentally clobber the mirror
 await axios.put(`${API}/api/restaurants/${restaurant.id}`, payload, {
 headers: { Authorization: `Bearer ${token}` },
 });
 await refreshReadiness();
 } catch (err) {
 setError(err?.response?.data?.detail || 'Could not save your restaurant info.');
 } finally {
 setSavingProfile(false);
 }
 };

 const onLogoFile = async (e) => {
 const file = e.target.files?.[0];
 if (!file) return;
 if (!/^image\/(jpe?g|png|webp)$/i.test(file.type)) {
 setError('Logo must be JPG, PNG or WebP.');
 return;
 }
 if (file.size > 10 * 1024 * 1024) {
 setError('Max file size is 10MB.');
 return;
 }
 setError('');
 try {
 const compressed = await compressImage(file, 1200);
 setCropperSrc(compressed);
 setCropperTarget('logo');
 } catch {
 setError('Could not process image, please try another one.');
 }
 if (e.target) e.target.value = '';
 };

 const persistLogo = async (dataUri) => {
 setSavingLogo(true);
 setError('');
 try {
 setLogoUrl(dataUri);
 const res = await axios.put(
 `${API}/api/restaurants/${restaurant.id}`,
 { logo_url: dataUri },
 { headers: { Authorization: `Bearer ${token}` } },
 );
 const savedUrl = res?.data?.logo_url;
 if (savedUrl) setLogoUrl(savedUrl);
 await refreshReadiness();
 } catch {
 setError('Could not upload the logo. Please try a different image.');
 } finally {
 setSavingLogo(false);
 }
 };

 const onCoverFile = async (e) => {
 const file = e.target.files?.[0];
 if (!file) return;
 if (!/^image\/(jpe?g|png|webp)$/i.test(file.type)) {
 setError('Main restaurant photo must be JPG, PNG or WebP.');
 return;
 }
 if (file.size > 10 * 1024 * 1024) {
 setError('Max file size is 10MB.');
 return;
 }
 setError('');
 try {
 const compressed = await compressImage(file, 1600);
 setCropperSrc(compressed);
 setCropperTarget('cover');
 } catch {
 setError('Could not process image, please try another one.');
 }
 if (e.target) e.target.value = '';
 };

 const persistCover = async (dataUri) => {
 setSavingCover(true);
 setError('');
 try {
 setProfile((p) => ({...p, cover_url: dataUri }));
 const res = await axios.put(
 `${API}/api/restaurants/${restaurant.id}`,
 { cover_url: dataUri },
 { headers: { Authorization: `Bearer ${token}` } },
 );
 const savedUrl = res?.data?.cover_url;
 if (savedUrl) setProfile((p) => ({...p, cover_url: savedUrl }));
 await refreshReadiness();
 } catch {
 setError('Could not upload the cover image. Please try a different image.');
 } finally {
 setSavingCover(false);
 }
 };

 const ensureDefaultCategory = async () => {
 // Fetch existing categories; if none, create a default "Menu" one.
 const cats = await axios.get(`${API}/api/restaurants/${restaurant.id}/categories`, {
 headers: { Authorization: `Bearer ${token}` },
 });
 if (Array.isArray(cats.data) && cats.data.length > 0) {
 return cats.data[0].id;
 }
 const created = await axios.post(
 `${API}/api/restaurants/${restaurant.id}/categories`,
 { name: 'Menu', order: 0, is_active: true },
 { headers: { Authorization: `Bearer ${token}` } },
 );
 return created.data?.id;
 };

 const onItemPhoto = async (e) => {
 const file = e.target.files?.[0];
 if (!file) return;
 if (!/^image\/(jpe?g|png|webp)$/i.test(file.type)) {
 setError('Item photo must be JPG, PNG or WebP.');
 return;
 }
 if (file.size > 10 * 1024 * 1024) {
 setError('Max file size is 10MB.');
 return;
 }
 setError('');
 try {
 const compressed = await compressImage(file);
 setCropperSrc(compressed);
 setCropperTarget('item');
 } catch {
 setError('Could not process image.');
 }
 if (e.target) e.target.value = '';
 };

 const onCropperSave = (dataUri) => {
 if (cropperTarget === 'logo') persistLogo(dataUri);
 else if (cropperTarget === 'cover') persistCover(dataUri);
 else if (cropperTarget === 'item') persistItemPhoto(dataUri);
 closeCropper();
 };

 // Pre-upload the cropped item photo to Cloudinary so the final
 // POST /api/restaurants/{id}/items request carries only a short
 // hosted URL (~100 bytes) instead of the full ~300–600 KB data URI.
 //
 // Why this matters: Cloudflare's WAF on the preview/production edge
 // rejects large inline payloads with `error code: 1010`, which makes
 // the "Add to menu" button appear broken from the owner's perspective.
 // Falls back to the legacy data-URI behaviour if the upload endpoint
 // is unavailable (e.g. Cloudinary not configured in dev).
 const persistItemPhoto = async (dataUri) => {
 if (!restaurant?.id) return;
 setError('');
 setItem((s) => ({...s, image_url: dataUri })); // optimistic preview
 try {
 const res = await axios.post(
 `${API}/api/uploads/image`,
 {
 data_uri: dataUri,
 folder: `restaurants/${restaurant.id}/items`,
 },
 { headers: { Authorization: `Bearer ${token}` } },
 );
 const hostedUrl = res?.data?.url;
 const isHosted = res?.data?.hosted === true;
 if (isHosted && hostedUrl) {
 setItem((s) => ({...s, image_url: hostedUrl }));
 }
 // If not hosted (Cloudinary not configured in preview), keep the
 // local data URI — POST /items will still accept it for small
 // images; for large ones we surface the WAF error inline below.
 } catch {
 // Upload failed — keep the data URI so the form still works for
 // small images. The POST /items endpoint retries server-side.
 }
 };

 const onAddItem = async (e) => {
 e?.preventDefault?.();
 if (!item.name.trim() ||!item.price) {
 setItemError('Please fill in both Item name and Price before adding the item.');
 return;
 }
 setSavingItem(true);
 setError('');
 setItemError('');
 try {
 const categoryId = await ensureDefaultCategory();
 await axios.post(
 `${API}/api/restaurants/${restaurant.id}/items`,
 {
 category_id: categoryId,
 name: item.name.trim(),
 description: item.description.trim(),
 price: parseFloat(item.price) || 0,
 image_url: item.image_url || '',
 is_available: true,
 order: 0,
 modifier_groups: [],
 },
 { headers: { Authorization: `Bearer ${token}` } },
 );
 setItem({ name: '', description: '', price: '', image_url: '' });
 await refreshReadiness();
 } catch (err) {
 const detail = err?.response?.data?.detail;
 const status = err?.response?.status;
 let message;
 if (status === 413 || status === 403) {
 // Cloudflare WAF / nginx rejection — almost always the image payload.
 message = 'The photo is too large for the server to accept. Please try a smaller image, or remove the photo and add the item without one.';
 } else if (detail) {
 message = detail;
 } else if (err?.message?.includes('Network')) {
 message = 'Network error — check your connection and try again.';
 } else {
 message = 'Could not add the item. Please try again.';
 }
 setError(message);
 setItemError(message);
 } finally {
 setSavingItem(false);
 }
 };

 const onGoLive = async () => {
 // iter132 — when the inline trial-activation card was visible from a
 // previous publish attempt, clicking Publish again hides it so the
 // owner sees a fresh response (either success or a NEW error).
 setNeedsTrialActivation(false);

 // iter132 — If the restaurant is already live (is_active + go_live_at),
 // ALWAYS hit the backend. The /api/onboarding/go-live endpoint is now
 // idempotent and returns {ok:true, already_live:true} immediately, which
 // sets liveResult and renders the success state with the
 // Grow My Restaurant celebration popup. Skipping the client-side
 // missingItems guard here is critical: newer required fields (e.g.
 // order_channels, fulfillment) added after the restaurant first went
 // live would otherwise trap the owner on the "Almost Ready!" modal
 // forever and they'd never see the celebration popup.
 const isAlreadyLive = !!(restaurant?.is_active && restaurant?.go_live_at);

 // Client-side guard: if the checklist still has missing items AND the
 // restaurant is NOT already live, jump the owner DIRECTLY to the first
 // missing field, focus it and paint the gold-pulse highlight so they
 // know exactly what to fix. iter132 — we deliberately do NOT open the
 // "Almost Ready!" modal anymore: owners reported it was confusing
 // ("a screen that has nothing to do with anything"). The new flow keeps
 // them inside the form — fix the field → fix the next → ... → click
 // Publish again → see the 🎉 Grow My Restaurant popup.
 if (!isAlreadyLive && missingItems.length > 0) {
 const first = missingItems[0];
 const target = CHECKLIST_TARGETS[first.key];
 const label = target?.label || first.label || 'a required field';
 toast.error(
 missingItems.length === 1
 ? `Just one thing left — fill in ${label} to publish.`
 : `${missingItems.length} required fields left — let's fix ${label} first.`,
 { duration: 4500 }
 );
 focusChecklistItem(first.key);
 return;
 }
 setGoingLive(true);
 setError('');
 try {
 const res = await axios.post(
 `${API}/api/onboarding/go-live`,
 {},
 { headers: { Authorization: `Bearer ${token}` } },
 );
 setLiveResult(res.data);
 } catch (err) {
 const d = err?.response?.data?.detail;
 // Backend returns { error, missing: [code1, code2,...] } for the
 // not_ready_to_go_live path. Translate those codes into the same
 // human labels we already show in the checklist so the error matches
 // the visual state of the page (no generic "Setup incomplete.").
 const FIELD_LABELS = {
 name: 'Restaurant name',
 phone: 'Phone number',
 address: 'Address',
 cuisine_type: 'Cuisine type',
 logo_url: 'Restaurant Logo',
 menu_items: 'At least 1 menu item',
 order_channels: 'At least 1 order receiving channel',
 fulfillment: 'Pickup or Delivery enabled',
 };
 let msg;
 // Subscription gate (402): the backend returned 402 because the trial
 // isn't active. iter132 — we now render a dedicated inline activation
 // card with a "Activate 45-day Free Trial" button that opens Stripe
 // Checkout directly. The owner NEVER leaves /onboarding/setup, so the
 // "Step 2 / blank screen" routing that previously happened is gone.
 if (err?.response?.status === 402 || d?.error === 'subscription_required') {
 setError('');
 setNeedsTrialActivation(true);
 // Smoothly scroll to the activation card so the owner sees it
 // immediately after pressing Publish (otherwise it lands above the
 // fold on long pages).
 setTimeout(() => {
 const el = document.querySelector('[data-testid="setup-trial-activation-card"]');
 if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
 }, 80);
 return;
 }
 if (d && typeof d === 'object') {
 const missing = Array.isArray(d.missing)? d.missing: [];
 if (missing.length) {
 const labels = missing.map((k) => FIELD_LABELS[k] || k).join(', ');
 msg = `Publishing blocked — fix these required fields: ${labels}.`;
 } else {
 msg = d.message || 'Setup incomplete.';
 }
 } else {
 msg = d || 'Could not go live.';
 }
 setError(msg);
 } finally {
 setGoingLive(false);
 }
 };

 /* iter132 — Direct Stripe Checkout for trial activation.
  * Skips the /complete-subscription page entirely (owners complained it
  * felt like a "blank/white Step 2 screen"). Opens the Stripe-hosted
  * Checkout URL in the same tab so the owner returns naturally to
  * /onboarding/setup after subscribing. */
 const onActivateTrial = async () => {
 setActivatingTrial(true);
 try {
 const origin = typeof window!== 'undefined'? window.location.origin: '';
 const res = await axios.post(
 `${API}/api/billing/create-checkout-session`,
 { origin_url: origin },
 { headers: { Authorization: `Bearer ${token}` } },
 );
 const url = res?.data?.url;
 if (!url) throw new Error('No checkout URL returned');
 window.location.href = url;
 } catch (e) {
 toast.error(
 e?.response?.data?.detail ||
 'Could not start the trial activation. Please try again in a moment.',
 );
 setActivatingTrial(false);
 }
 };

 const sections = readiness?.sections || {};
 const progressPercent = readiness?.progress_percent?? 0;

 // Granular publication checklist — uses the field-level data the backend
 // already returns in /api/onboarding/readiness (sections.profile.fields.*,
 // sections.logo.ok, sections.menu.count, etc.) so the owner sees EXACTLY
 // which fields are blocking go-live, never a generic "Restaurant info"
 // bucket message.
 const checklist = useMemo(() => {
 const f = sections?.profile?.fields || {};
 const cats = sections?.categories || {};
 const menu = sections?.menu || {};
 return [
 { key: 'name', label: 'Restaurant name', ok:!!f.name, group: 'Restaurant info' },
 { key: 'phone', label: 'Phone number', ok:!!f.phone, group: 'Restaurant info' },
 { key: 'address', label: 'Address', ok:!!f.address, group: 'Restaurant info' },
 { key: 'cuisine_type', label: 'Cuisine type', ok:!!f.cuisine_type, group: 'Restaurant info' },
 { key: 'logo', label: 'Logo uploaded', ok:!!sections?.logo?.ok, group: 'Branding' },
 { key: 'hours', label: 'Business hours confirmed', ok:!!sections?.business_hours?.ok, group: 'Hours' },
 { key: 'categories', label: cats.ok? `Menu categories (${cats.count || 0})`: 'Menu categories missing', ok:!!cats.ok, group: 'Menu' },
 { key: 'items', label: menu.ok? `Menu items (${menu.count || 0})`: 'Menu items missing', ok:!!menu.ok, group: 'Menu' },
 { key: 'order_channels', label: sections?.order_channels?.ok? 'Order receiving channels configured': 'At least 1 order receiving channel enabled', ok:!!sections?.order_channels?.ok, group: 'Order Channels' },
 { key: 'fulfillment', label: sections?.fulfillment?.ok? 'Pickup or Delivery enabled': 'Enable Pickup or Delivery', ok:!!sections?.fulfillment?.ok, group: 'Fulfillment' },
 ];
 }, [sections]);

 const missingItems = useMemo(() => checklist.filter((c) =>!c.ok), [checklist]);
 const completedCount = checklist.length - missingItems.length;
 // `ready` drives the gold styling on the publish button + the
 // "Everything looks good" summary copy. iter132 — restaurants that are
 // already live (is_active + go_live_at) are ALWAYS considered ready,
 // regardless of whether newer required fields (added after they first
 // went live) are still missing on the readiness payload.
 const ready = !!(
 (restaurant?.is_active && restaurant?.go_live_at) ||
 (readiness?.ready && missingItems.length === 0)
 );

 // Auto-scroll back to the publish checklist as soon as the task the
 // owner navigated to from a "Go to..." button is satisfied. Without
 // this they had to manually scroll back down the page after every
 // edit-and-save cycle, then up again to fix the next item.
 //
 // Important: we use a ref + raw setTimeout (NOT useEffect cleanup)
 // because `checklist` is recomputed on every `readiness` refresh —
 // a useEffect cleanup would clearTimeout on every readiness change,
 // making the 600ms delay never fire while autosaves are running.
 const scrollBackTimerRef = useRef(null);
 useEffect(() => {
 if (!pendingChecklistKey) return;
 const item = checklist.find((c) => c.key === pendingChecklistKey);
 if (!item ||!item.ok) return;
 // Schedule once per pending key — subsequent re-renders are ignored
 // until this timer fires or the key changes.
 if (scrollBackTimerRef.current) return;
 scrollBackTimerRef.current = setTimeout(() => {
 const target = document.querySelector('[data-testid="setup-publish-checklist"]');
 if (target) {
 target.scrollIntoView({ behavior: 'smooth', block: 'center' });
 const completed = document.querySelector(
 `[data-testid="publish-check-${pendingChecklistKey}"]`
 );
 if (completed) {
 completed.classList.add('nf-checklist-just-done');
 setTimeout(() => completed.classList.remove('nf-checklist-just-done'), 2200);
 }
 }
 scrollBackTimerRef.current = null;
 setPendingChecklistKey(null);
 }, 600);
 }, [checklist, pendingChecklistKey]);

 // Cancel any in-flight scroll-back if the user clicks a different
 // "Go to..." button before the previous return-scroll fires.
 useEffect(() => {
 return () => {
 if (scrollBackTimerRef.current) {
 clearTimeout(scrollBackTimerRef.current);
 scrollBackTimerRef.current = null;
 }
 };
 }, []);

 /* iter141 — Stripe Checkout return handler (Step 4 → Step 5).
  *
  * The 45-day free trial activation is now Step 4 of the onboarding
  * wizard (its own dedicated /onboarding/trial page). Stripe still
  * returns to `/onboarding/setup?activated=1&session_id=cs_...` on
  * success — we finalise the session server-side (so the
  * subscription/customer IDs land even if the Stripe webhook is
  * delayed), refresh the restaurant doc so `subscription_status` is
  * accurate, and greet the owner with a success toast. We DO NOT
  * auto-publish anymore: the owner still has to complete Step 5
  * (Restaurant Setup) BEFORE the Publish button can go live. This is
  * the whole point of the reorganised sequence — financial steps
  * before publication.
  *
  * Idempotent: protected by a ref + the activation query params are
  * stripped after the first run, so refreshes never re-trigger it.
  */
 useEffect(() => {
 if (trialAutoPublishFiredRef.current) return;
 const activated = searchParams.get('activated');
 const sessionId = searchParams.get('session_id');
 if (activated!== '1' ||!sessionId) return;
 if (!restaurant?.id ||!token) return; // wait for the page to be hydrated

 trialAutoPublishFiredRef.current = true;
 (async () => {
 setFinalizingTrial(true);
 try {
 // 1) Finalise the Stripe session server-side (webhook fallback).
 // Idempotent — safe even if the webhook has already landed.
 await axios.get(
 `${API}/api/billing/finalize-session/${encodeURIComponent(sessionId)}`,
 { headers: { Authorization: `Bearer ${token}` } },
 ).catch(() => null);

 // 2) Re-fetch the restaurant doc so `subscription_status` reflects
 // the now-active trial. Also unblocks the /go-live gate later
 // when the owner completes setup and presses Publish.
 try {
 const r = await axios.get(`${API}/api/restaurants/${restaurant.id}`);
 if (r?.data?.restaurant) setRestaurant(r.data.restaurant);
 } catch (e) { /* ignore */ }

 // 3) Celebrate the trial + let the owner keep working through
 // the setup form. No auto-publish — Step 5 is still ahead.
 toast.success('🎉 45-day free trial activated!', {
 description: 'Now complete your restaurant setup below, then hit Publish.',
 duration: 5000,
 });
 } finally {
 setFinalizingTrial(false);
 // Strip the query params so refreshes don't re-fire the flow.
 setSearchParams({}, { replace: true });
 }
 })();
 }, [restaurant?.id, restaurant, token, searchParams, setSearchParams]);

 // Wraps focusChecklistItem so the auto-return effect knows which task
 // to watch. Used by all "Go to..." buttons in the checklist /
 // Remaining Tasks panel.
 //
 // Menu + categories are special-cased: the full menu builder lives in
 // the Dashboard "Menu" tab (single source of truth), so those tasks
 // route the owner away from the onboarding wizard instead of scrolling
 // within it. The owner returns to the wizard via the OnboardingShell
 // breadcrumb / "Go live" button.
 const goToChecklistItem = (key) => {
 if (key === 'items' || key === 'categories') {
 openMenuManager();
 return;
 }
 setPendingChecklistKey(key);
 focusChecklistItem(key);
 };

 if (loading) {
 return (
 <div
 className="min-h-screen bg-[#0A0A0A] flex items-center justify-center"
 data-testid="onboarding-setup-loading"
 >
 <Loader2 className="w-7 h-7 text-yellow-400 animate-spin" />
 </div>
 );
 }

 /* iter132 — Full-screen overlay shown for ~1-3s right after the Stripe
  * Checkout success redirect lands back here. We're (1) finalising the
  * session, (2) refreshing the restaurant doc, and (3) auto-firing the
  * publish call. The instant publish succeeds, `liveResult.ok` flips
  * true and the success view (with the 🎉 Grow My Restaurant popup)
  * replaces this overlay. */
 if (finalizingTrial && !liveResult?.ok) {
 return (
 <div
 className="min-h-screen bg-[#0A0A0A] flex flex-col items-center justify-center px-6 text-center"
 data-testid="onboarding-trial-finalizing"
 >
 <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-yellow-500/15 border border-yellow-500/35 text-yellow-300 text-[10.5px] uppercase tracking-[0.22em] font-bold mb-4">
 <Sparkles className="w-3 h-3" /> Trial activated
 </div>
 <h2 className="font-['Outfit',_Inter,_sans-serif] text-[26px] sm:text-[30px] font-semibold tracking-tight text-white">
 Publishing your restaurant…
 </h2>
 <p className="mt-2 max-w-sm text-[13.5px] text-white/55 leading-relaxed">
 Your 45-day free trial is active. Hang tight — we're putting you live.
 </p>
 <Loader2 className="mt-6 w-7 h-7 text-yellow-400 animate-spin" />
 </div>
 );
 }

 // SUCCESS STATE — restaurant is live
 if (liveResult?.ok) {
 const menuUrl = liveResult.menu_url || `/menu/${liveResult.slug || ''}`;
 return (
 <div
 className="min-h-screen bg-[#0A0A0A] text-white antialiased flex items-center justify-center px-4"
 data-testid="onboarding-setup-live"
 >
 <div className="max-w-xl w-full text-center">
 <div className="mx-auto w-20 h-20 rounded-2xl bg-gradient-to-b from-yellow-300 to-yellow-500 text-black flex items-center justify-center shadow-[0_24px_60px_-12px_rgba(250,204,21,0.6)]">
 <Rocket className="w-9 h-9" strokeWidth={2.2} />
 </div>
 <h1 className="mt-7 font-['Outfit',_Inter,_sans-serif] text-4xl sm:text-5xl font-semibold tracking-[-0.03em] leading-[1.05]">
 You're <span className="bg-gradient-to-r from-yellow-300 via-yellow-400 to-amber-500 bg-clip-text text-transparent">live</span>.
 </h1>
 <p className="mt-4 text-[15.5px] text-white/65 leading-relaxed">
 Your restaurant is now public on NoFee. Share your menu link with customers and start
 taking orders — 0% commission, paid directly to your bank.
 </p>
 <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
 <a
 href={menuUrl}
 target="_blank"
 rel="noreferrer"
 data-testid="setup-view-public-menu"
 className="inline-flex items-center justify-center gap-2 px-7 py-4 min-h-[56px] rounded-2xl bg-gradient-to-b from-yellow-300 to-yellow-500 hover:from-yellow-200 hover:to-yellow-400 text-black font-extrabold text-[15px] tracking-tight transition-all shadow-[0_18px_50px_-12px_rgba(250,204,21,0.55)] hover:-translate-y-0.5"
 >
 View my public menu <ExternalLink className="w-4 h-4" />
 </a>
 <button
 type="button"
 onClick={() => navigate('/dashboard')}
 data-testid="setup-go-to-dashboard"
 className="inline-flex items-center justify-center gap-2 px-6 py-4 min-h-[56px] rounded-2xl border border-white/15 hover:border-white/30 text-white/85 hover:text-white font-bold text-[14px] transition-all"
 >
 Go to dashboard <ArrowRight className="w-4 h-4" />
 </button>
 </div>
 </div>

 {/* 🎉 Grow My Restaurant popup — fires once, right after Go Live */}
 <GoLiveGrowPopup />
 </div>
 );
 }

 return (
 <OnboardingShell
 currentStep="setup"
 backFallback="/onboarding"
 testid="onboarding-setup-page"
 >
 {/* iter115 + iter123 — Locked banner. Two states:
 •!token → Step 1 (Create account) required
 • token, no Stripe → Step 2 (Stripe connect) required (legacy)
 The owner can still scroll/read everything below, but the
 click-shield overlay intercepts any tap and shows a toast. */}
 {!loading &&!stripeReady && (
 <div
 data-testid="setup-stripe-lock-banner"
 className="mb-6 rounded-2xl border border-amber-400/40 bg-gradient-to-br from-amber-500/[0.14] to-yellow-500/[0.06] p-4 sm:p-5 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between"
 >
 <div className="flex items-start gap-3 min-w-0">
 <div className="w-10 h-10 rounded-xl bg-amber-500/15 border border-amber-400/40 flex items-center justify-center shrink-0">
 <Lock className="w-4 h-4 text-amber-300" />
 </div>
 <div className="min-w-0">
 <p className="text-[10px] uppercase tracking-[0.22em] text-amber-300 font-bold">
 {!token? 'Setup locked · Step 1 required': 'Setup locked · Step 2 required'}
 </p>
 <p className="mt-1 text-[14px] sm:text-[14.5px] text-white font-semibold leading-snug">
 {!token
? 'Create your account to unlock your restaurant setup.'
: 'Connect Stripe to unlock your restaurant setup.'}
 </p>
 <p className="mt-1 text-[12.5px] text-white/65 leading-relaxed">
 You can preview this page, but uploads, menu edits, hours, and the Go live
 button stay locked until {!token? 'your account is created': 'Stripe payouts are active'}.
 </p>
 </div>
 </div>
 <button
 type="button"
 onClick={() => navigate(!token? '/onboarding/account': '/onboarding/stripe-connect')}
 data-testid="setup-stripe-lock-cta"
 className="shrink-0 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-b from-yellow-300 to-yellow-500 hover:from-yellow-200 hover:to-yellow-400 text-black text-[13.5px] font-extrabold tracking-tight shadow-[0_12px_30px_-10px_rgba(250,204,21,0.55)] transition-all"
 >
 {!token? 'Go to Step 1 — Account': 'Go to Step 2 — Stripe'}
 <ArrowRight className="w-4 h-4" />
 </button>
 </div>
 )}

 {/* The "locked" wrapper. When Stripe isn't ready we soften the
 content (opacity + saturation) and lay a transparent shield on
 top that captures every click and surfaces the toast. Keyboard
 tabbing through inputs is also disabled because the inner
 fieldset is disabled. */}
 <div className={!stripeReady &&!loading? 'relative': ''}>
 {!stripeReady &&!loading && (
 <div
 data-testid="setup-stripe-lock-shield"
 role="button"
 tabIndex={0}
 aria-label={lockMessage}
 onClick={onLockedClick}
 onKeyDown={(e) => {
 if (e.key === 'Enter' || e.key === ' ') {
 e.preventDefault();
 onLockedClick();
 }
 }}
 className="absolute inset-0 z-30 cursor-not-allowed rounded-2xl"
 />
 )}
 <fieldset
 disabled={!stripeReady &&!loading}
 data-testid="setup-content"
 className={!stripeReady &&!loading
? 'opacity-60 saturate-[0.85] transition-opacity duration-200'
: 'transition-opacity duration-200'}
 >
 {/* Header */}
 <div>
 <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-yellow-500/30 bg-yellow-500/[0.08]">
 <Sparkles className="w-3.5 h-3.5 text-yellow-300" />
 <span
 className="text-[10.5px] uppercase tracking-[0.22em] text-yellow-300 font-bold"
 data-testid="setup-progress-pill"
 >
 Setup · {progressPercent}% complete
 </span>
 </div>
 <h1
 data-testid="setup-headline"
 className="mt-5 font-['Outfit',_Inter,_sans-serif] text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-[-0.03em] leading-[1.05] text-white"
 >
 Welcome to NoFee — <span className="bg-gradient-to-r from-yellow-300 via-yellow-400 to-amber-500 bg-clip-text text-transparent">let's set up your restaurant</span>.
 </h1>
 <p className="mt-4 text-[15px] sm:text-base text-white/65 leading-relaxed max-w-2xl">
 Complete the steps below to start receiving orders. Everything can be edited later
 from your dashboard.
 </p>

 {/* Progress bar (matches the dashboard status hero) */}
 <div className="mt-6 h-1.5 rounded-full bg-white/[0.06] overflow-hidden" aria-hidden="true">
 <div
 data-testid="setup-progress-bar"
 className="h-full bg-gradient-to-r from-yellow-300 via-yellow-400 to-amber-500 transition-all duration-700 ease-out"
 style={{ width: `${progressPercent}%` }}
 />
 </div>
 </div>

 {error && (
 <div
 data-testid="setup-error"
 className="mt-6 flex items-start gap-3 p-4 rounded-2xl border border-red-500/30 bg-red-500/[0.06] text-red-200 text-[13.5px]"
 >
 <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
 <span>{error}</span>
 </div>
 )}

 {/* iter132 — Trial activation card. Surfaced when the publish API
     returned HTTP 402 (subscription_required). Single-click button
     opens Stripe Checkout in the same tab. Replaces the legacy redirect
     to /complete-subscription that owners reported as a confusing
     "blank Step 2 screen". */}
 {needsTrialActivation && (
 <div
 data-testid="setup-trial-activation-card"
 className="mt-6 rounded-2xl border border-yellow-500/40 bg-gradient-to-br from-yellow-500/[0.10] via-amber-500/[0.06] to-transparent p-5 sm:p-6 shadow-[0_24px_60px_-24px_rgba(250,204,21,0.35)]"
 >
 <div className="flex items-start gap-3.5">
 <div className="w-10 h-10 rounded-xl bg-yellow-500/20 border border-yellow-500/45 flex items-center justify-center shrink-0">
 <Sparkles className="w-5 h-5 text-yellow-300" strokeWidth={1.8} />
 </div>
 <div className="min-w-0 flex-1">
 <div className="text-[10.5px] uppercase tracking-[0.22em] text-yellow-300 font-bold">
 One last step
 </div>
 <h3 className="mt-1 font-['Outfit',_Inter,_sans-serif] text-[18px] sm:text-[20px] font-semibold tracking-tight text-white">
 Activate your 45-day free trial to publish
 </h3>
 <p className="mt-2 text-[13.5px] text-white/65 leading-relaxed">
 Your restaurant setup is complete. Activate the trial below and
 you'll be live in seconds — no charge for the first 45 days, cancel
 any time.
 </p>
 </div>
 </div>
 <button
 type="button"
 onClick={onActivateTrial}
 disabled={activatingTrial}
 data-testid="setup-activate-trial-btn"
 className="mt-5 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 min-h-[52px] rounded-2xl bg-gradient-to-b from-yellow-300 to-yellow-500 hover:from-yellow-200 hover:to-yellow-400 text-black font-extrabold text-[14.5px] tracking-tight disabled:opacity-60 disabled:cursor-not-allowed transition-all shadow-[0_18px_40px_-16px_rgba(250,204,21,0.55)] hover:-translate-y-0.5"
 >
 {activatingTrial? (
 <Loader2 className="w-4 h-4 animate-spin" />
 ): (
 <Sparkles className="w-4 h-4" />
 )}
 {activatingTrial? 'Opening Stripe…': 'Activate 45-day Free Trial'}
 {!activatingTrial && <ArrowRight className="w-4 h-4" />}
 </button>
 </div>
 )}

 {/* Section A — Profile */}
 <div className="mt-8 space-y-6">
 <SectionShell
 idx={1}
 title="Restaurant info"
 subtitle="The basics customers need to find you and call you."
 ok={!!sections?.profile?.ok}
 icon={Building2}
 testid="setup-section-profile"
 >
 <form onSubmit={onSaveProfile} className="space-y-4">
 <div className="grid sm:grid-cols-2 gap-4">
 <Field label="Restaurant name">
 <input
 data-testid="setup-input-name"
 type="text"
 value={profile.name}
 onChange={(e) => setProfile({...profile, name: e.target.value })}
 placeholder="e.g. Tony's Pizza"
 className={inputCls}
 />
 </Field>
 <Field label="Phone">
 <input
 data-testid="setup-input-phone"
 type="tel"
 value={profile.phone}
 onChange={(e) => setProfile({...profile, phone: e.target.value })}
 placeholder="+1 555 010 0199"
 className={inputCls}
 />
 </Field>
 </div>
 <Field label="Street address" hint="Street and number only — city, state and ZIP go in the fields below.">
 <input
 data-testid="setup-input-address"
 type="text"
 value={profile.address}
 onChange={(e) => setProfile({...profile, address: e.target.value })}
 placeholder="123 Main St"
 className={inputCls}
 />
 </Field>
 <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
 <Field label="ZIP code" hint="Type your ZIP — we'll auto-fill City and State.">
 <input
 data-testid="setup-input-zip"
 type="text"
 inputMode="numeric"
 maxLength={10}
 value={profile.zip_code || ''}
 onChange={(e) => {
 const cleaned = e.target.value.replace(/[^0-9\-]/g, '').slice(0, 10);
 setProfile({...profile, zip_code: cleaned });
 if (zipLookup.warning) zipLookup.clearWarning();
 }}
 onBlur={async () => {
 const r = await zipLookup.resolve(profile.zip_code, profile.state);
 if (r.valid &&!r.mismatch) {
 setProfile((p) => ({
...p,
 state: r.state || p.state,
 city: p.city || r.city || '',
 }));
 }
 }}
 placeholder="34746"
 className={inputCls}
 />
 </Field>
 <Field label="City">
 <input
 data-testid="setup-input-city"
 type="text"
 value={profile.city || ''}
 onChange={(e) => setProfile({...profile, city: e.target.value })}
 placeholder="Kissimmee"
 className={inputCls}
 />
 </Field>
 </div>
 <Field
 label="State"
 hint="Required for automatic sales tax calculation."
 >
 <div className="relative">
 <select
 data-testid="setup-input-state"
 value={profile.state || ''}
 onChange={async (e) => {
 const next = e.target.value;
 setProfile({...profile, state: next });
 if (profile.zip_code) {
 await zipLookup.resolve(profile.zip_code, next);
 }
 }}
 className={`${inputCls} appearance-none pr-10 cursor-pointer`}
 >
 <option value="">— Select state —</option>
 {US_STATES.map((s) => (
 <option key={s.code} value={s.code}>{`${s.code} — ${s.name}`}</option>
 ))}
 </select>
 <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-slate-400">▾</div>
 </div>
 </Field>
 {zipLookup.warning && (
 <div
 data-testid="setup-zip-warning"
 className="-mt-2 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700"
 role="alert"
 >
 {zipLookup.warning}
 </div>
 )}
 <Field
 label="Cuisine types"
 hint="Pick up to 5 cuisines you serve. Used by customers in search & filters."
 >
 <CuisineTypeSelector
 value={profile.cuisine_types || []}
 onChange={(next) => setProfile({...profile, cuisine_types: next })}
 variant="dark"
 testid="setup-cuisine-types"
 />
 </Field>
 <Field
 label="Short description"
 hint="Optional — one sentence that shows on your menu page."
 >
 <textarea
 data-testid="setup-input-description"
 rows={2}
 value={profile.description}
 onChange={(e) => setProfile({...profile, description: e.target.value })}
 placeholder="Wood-fired pizza & fresh pasta — family-run since 1998."
 className={inputCls}
 />
 </Field>

 {/* Main Restaurant Photo — shown in directory search results
 and at the top of the public menu. Renamed from "Cover image"
 to make the purpose obvious for restaurant owners. */}
 <Field
 label="Main Restaurant Photo"
 hint="This photo appears in search results and at the top of your restaurant page. 16:9 looks best. JPG/PNG/WebP, max 8MB."
 >
 <div className="flex items-center gap-3">
 {profile.cover_url? (
 <img
 src={profile.cover_url}
 alt="Main restaurant photo"
 className="w-24 h-14 rounded-lg object-cover border border-white/10"
 data-testid="setup-cover-preview"
 />
 ): (
 <div className="w-24 h-14 rounded-lg bg-white/[0.04] border border-white/10 flex items-center justify-center text-white/40 text-[10px]">
 No image
 </div>
 )}
 <input
 ref={fileCoverRef}
 type="file"
 accept="image/jpeg,image/png,image/webp"
 className="hidden"
 onChange={onCoverFile}
 data-testid="setup-cover-file"
 />
 <button
 type="button"
 onClick={() => fileCoverRef.current?.click()}
 disabled={savingCover}
 data-testid="setup-cover-upload"
 className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-white/15 hover:border-white/30 text-white/85 hover:text-white font-bold text-[12.5px] transition-all disabled:opacity-60"
 >
 {savingCover? <Loader2 className="w-3.5 h-3.5 animate-spin" />: null}
 {profile.cover_url? 'Replace main photo': 'Upload main photo'}
 </button>
 </div>
 {/* Live preview — exactly how the photo looks in the
 /restaurants search results so the owner can iterate
 on the upload before publishing. */}
 <CoverPhotoPreview
 coverUrl={profile.cover_url}
 name={profile.name}
 cuisine={(profile.cuisine_types && profile.cuisine_types[0]) || profile.cuisine_type}
 testid="setup-cover-preview-card"
 />
 </Field>

 {/* Delivery fee + minimum order — operational pricing. The
 Delivery Fee hint clarifies it's only used when delivery
 is enabled in the Order Fulfillment section below. */}
 <div className="grid sm:grid-cols-2 gap-4">
 <Field
 label="Delivery Fee ($)"
 hint="Charged to the customer at checkout when they pick delivery."
 >
 <MoneyInput
 testid="setup-input-delivery-fee"
 value={profile.delivery_fee}
 onChange={(raw) => setProfile({...profile, delivery_fee: raw })}
 onCommit={(n) => setProfile({...profile, delivery_fee: n === ''? 0: n })}
 inputClassName={inputCls}
 prefix=""
 />
 </Field>
 <Field
 label="Minimum Order ($)"
 hint="Smallest order subtotal you'll accept. Set to 0 for no minimum."
 >
 <MoneyInput
 testid="setup-input-min-order"
 value={profile.min_order}
 onChange={(raw) => setProfile({...profile, min_order: raw })}
 onCommit={(n) => setProfile({...profile, min_order: n === ''? 0: n })}
 inputClassName={inputCls}
 prefix=""
 />
 </Field>
 </div>

 {/* ─── Delivery Radius ─────────────────────────────────
 Maximum distance (miles) from the restaurant ZIP to
 the customer's delivery ZIP. The customer checkout
 blocks the Pay button when the typed address is
 outside this radius. Default = 5 mi. */}
 <Field
 label="Delivery Radius (Miles)"
 hint="Maximum distance from your restaurant that you are willing to deliver."
 >
 <div className="relative">
 <select
 data-testid="setup-input-delivery-radius"
 value={profile.delivery_radius_miles?? DEFAULT_DELIVERY_RADIUS}
 onChange={(e) => setProfile({...profile, delivery_radius_miles: Number(e.target.value) })}
 className={`${inputCls} appearance-none pr-10 cursor-pointer`}
 >
 {DELIVERY_RADIUS_OPTIONS.map((o) => (
 <option key={o.value} value={o.value}>{o.label}</option>
 ))}
 </select>
 <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-slate-400">▾</div>
 </div>
 </Field>

 {/* Accepted Payment Methods — Stripe is required (always-on);
 is optional via the toggle. Matches the same UX
 shown in Dashboard → Settings. */}
 <div className="rounded-2xl border border-white/10 bg-white/[0.025] p-4">
 <p className="text-[13.5px] font-bold text-white">Accepted Payment Methods</p>
 <p className="text-[12px] text-white/55 mt-1">
 Stripe is always available for credit cards, Apple Pay and Google Pay. can be enabled as an additional payment option.
 </p>
 <div className="mt-3 flex items-start gap-3 p-3 rounded-xl border border-emerald-500/40 bg-emerald-500/[0.07]" data-testid="setup-payment-stripe-row">
 <span className="mt-0.5 inline-flex w-5 h-5 rounded-full bg-emerald-500 items-center justify-center flex-shrink-0">
 <Check className="w-3 h-3 text-white" strokeWidth={3} />
 </span>
 <span className="text-[13px] text-white">
 <span className="block font-semibold">
 Stripe <span className="text-white/55 font-normal">(Cards, Apple Pay, Google Pay)</span>
 </span>
 <span className="block text-[11.5px] text-emerald-300 font-semibold mt-0.5">
 Enabled by default · Required
 </span>
 </span>
 </div>
 </div>

 <div className="pt-1 flex items-center gap-3 flex-wrap">
 <button
 type="submit"
 disabled={savingProfile}
 data-testid="setup-save-profile"
 className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-white text-black hover:bg-white/90 disabled:opacity-60 font-bold text-[13.5px] transition-all"
 >
 {savingProfile? (
 <Loader2 className="w-4 h-4 animate-spin" />
 ): sections?.profile?.ok? (
 <Check className="w-4 h-4" />
 ): null}
 {sections?.profile?.ok? 'Update info': 'Save info'}
 </button>
 <SaveStatusPill
 status={profileSaveStatus}
 error={profileSaveError}
 variant="dark"
 />
 </div>
 </form>
 </SectionShell>

 {/* Section B — Restaurant Logo */}
 <SectionShell
 idx={2}
 title="Restaurant Logo"
 subtitle="Your business logo shown throughout the platform — menu page, receipts, and order notifications."
 ok={!!sections?.logo?.ok}
 icon={ImageIcon}
 testid="setup-section-logo"
 >
 <div className="flex items-center gap-5">
 <div
 data-testid="setup-logo-preview"
 className="w-24 h-24 rounded-2xl border border-white/12 bg-black/40 flex items-center justify-center overflow-hidden shrink-0"
 >
 {logoUrl? (
 <img src={logoUrl} alt="Logo preview" className="w-full h-full object-cover" />
 ): (
 <ImageIcon className="w-7 h-7 text-white/30" strokeWidth={1.5} />
 )}
 </div>
 <div className="flex-1">
 <input
 ref={fileLogoRef}
 type="file"
 accept="image/jpeg,image/png,image/webp"
 onChange={onLogoFile}
 className="hidden"
 data-testid="setup-logo-file"
 />
 <button
 type="button"
 onClick={() => fileLogoRef.current?.click()}
 disabled={savingLogo}
 data-testid="setup-logo-upload-btn"
 className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-white text-black hover:bg-white/90 disabled:opacity-60 font-bold text-[13.5px] transition-all"
 >
 {savingLogo? (
 <Loader2 className="w-4 h-4 animate-spin" />
 ): (
 <ImageIcon className="w-4 h-4" />
 )}
 {logoUrl? 'Replace logo': 'Upload logo'}
 </button>
 <p className="mt-2 text-[11.5px] text-white/40">
 JPG, PNG or WebP · up to 8MB · square works best.
 </p>
 </div>
 </div>
 </SectionShell>

 {/* Section — Business Hours */}
 <SectionShell
 idx={3}
 title="Business hours"
 subtitle="When is your restaurant open? Customers see this on your menu."
 ok={!!sections?.business_hours?.ok}
 icon={Clock}
 testid="setup-section-hours"
 >
 <div className="space-y-2.5">
 {DAY_KEYS.map((d) => {
 const h = hours[d] || { closed: false, open: '11:00', close: '22:00' };
 const isOpen =!h.closed;
 return (
 <div
 key={d}
 data-testid={`setup-hours-row-${d}`}
 className="grid grid-cols-[110px_1fr] sm:grid-cols-[140px_auto_1fr_1fr] items-center gap-3 p-3 rounded-xl border border-white/8 bg-white/[0.02]"
 >
 <p className="text-[13.5px] font-semibold text-white">{DAY_LABELS[d]}</p>
 {/* Segmented Open/Closed toggle — clearer than a single checkbox.
 "Open" → reveals the time inputs.
 "Closed" → hides them and shows "Closed all day". */}
 <div
 role="radiogroup"
 aria-label={`${DAY_LABELS[d]} availability`}
 className="inline-flex p-0.5 rounded-lg border border-white/10 bg-black/40 shrink-0"
 >
 <button
 type="button"
 role="radio"
 aria-checked={isOpen}
 data-testid={`setup-hours-open-${d}`}
 onClick={() => setHours({...hours, [d]: {...h, closed: false } })}
 className={`px-3 py-1 rounded-md text-[11.5px] font-bold transition-colors ${
 isOpen
? 'bg-yellow-400 text-black'
: 'text-white/55 hover:text-white/80'
 }`}
 >
 Open
 </button>
 <button
 type="button"
 role="radio"
 aria-checked={!isOpen}
 data-testid={`setup-hours-closed-${d}`}
 onClick={() => setHours({...hours, [d]: {...h, closed: true } })}
 className={`px-3 py-1 rounded-md text-[11.5px] font-bold transition-colors ${
!isOpen
? 'bg-white/15 text-white'
: 'text-white/55 hover:text-white/80'
 }`}
 >
 Closed
 </button>
 </div>
 {isOpen? (
 <>
 <input
 type="time"
 data-testid={`setup-hours-open-time-${d}`}
 value={h.open}
 onChange={(e) => setHours({...hours, [d]: {...h, open: e.target.value } })}
 className={inputCls}
 />
 <input
 type="time"
 data-testid={`setup-hours-close-time-${d}`}
 value={h.close}
 onChange={(e) => setHours({...hours, [d]: {...h, close: e.target.value } })}
 className={inputCls}
 />
 </>
 ): (
 <span
 data-testid={`setup-hours-closed-label-${d}`}
 className="sm:col-span-2 text-[12.5px] italic text-white/40"
 >
 Closed all day
 </span>
 )}
 </div>
 );
 })}
 </div>
 <div className="pt-4 flex items-center gap-3 flex-wrap">
 <button
 type="button"
 onClick={onSaveHours}
 disabled={savingHours}
 data-testid="setup-save-hours-btn"
 className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-white text-black hover:bg-white/90 disabled:opacity-60 font-bold text-[13.5px] transition-all"
 >
 {savingHours? <Loader2 className="w-4 h-4 animate-spin" />: <Check className="w-4 h-4" />}
 Save business hours
 </button>
 <SaveStatusPill
 status={hoursSaveStatus}
 error={hoursSaveError}
 variant="dark"
 />
 </div>

 {/* Holiday Hours & Special Dates — overrides regular weekly hours */}
 <div className="mt-8 pt-6 border-t border-white/[0.06]" data-testid="setup-holiday-hours-block">
 <HolidayHoursManager token={token} restaurantId={restaurant?.id} />
 </div>

 {/* ─── Restaurant Status (manual override) ────────────────
 Lets the owner pause new orders without changing
 business hours. Saves via the same debounced PUT used
 by Profile / Order Fulfillment. */}
 <div className="mt-8 pt-6 border-t border-white/[0.06]" data-testid="setup-restaurant-status-block">
 <p className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-yellow-300/80 mb-1">
 Restaurant Status
 </p>
 <h4 className="text-[15px] font-semibold text-white">
 Currently accepting new orders?
 </h4>
 <p className="mt-1 text-[12.5px] text-white/55 leading-relaxed mb-3">
 Temporarily pause new orders when the kitchen needs to catch up. Your menu stays visible to customers — only checkout is disabled.
 </p>
 <RestaurantStatusControl
 value={profile.manual_status}
 until={profile.manual_status_until}
 onChange={async (s, untilISO) => {
 // Update local state immediately for snappy UX, then
 // persist independently via a PATCH-style PUT. Auto-resume
 // timestamp is sent as null when the owner picks "manual".
 setProfile((p) => ({...p, manual_status: s, manual_status_until: untilISO }));
 if (!restaurant?.id) return;
 try {
 await axios.put(
 `${API}/api/restaurants/${restaurant.id}`,
 { manual_status: s, manual_status_until: untilISO },
 { headers: { Authorization: `Bearer ${token}` } },
 );
 } catch (err) {
 setError(err?.response?.data?.detail || 'Could not update restaurant status.');
 }
 }}
 testid="setup-restaurant-status"
 />
 </div>
 </SectionShell>

 {/* Section — Menu (CTA only)
 We intentionally removed the inline category + first-item
 forms from the onboarding wizard (2026-06-09). The Dashboard
 "Menu" tab is the single source of truth for menu CRUD —
 add/edit items, modifiers, extras, photos, prices, etc.
 Keeping a tiny editor here was causing confusion: owners
 created "Pizza · Cheese" in onboarding then re-discovered
 the same thing in the Dashboard Menu tab and didn't know
 which one was canonical. */}
 <SectionShell
 idx={4}
 title="Build your menu"
 subtitle={
 sections?.menu?.ok && sections?.categories?.ok
? `${sections?.categories?.count || 0} categor${(sections?.categories?.count || 0) === 1? 'y': 'ies'} · ${sections?.menu?.count || 0} item${(sections?.menu?.count || 0) === 1? '': 's'} created. Open the Menu Manager anytime to add more, set photos, modifiers and extras.`
: 'You need at least 1 category and 1 menu item before you can publish. The full menu builder — categories, photos, modifiers, extras, prices — lives in your Dashboard Menu Manager.'
 }
 ok={!!sections?.menu?.ok &&!!sections?.categories?.ok}
 icon={UtensilsCrossed}
 testid="setup-section-menu"
 >
 <div className="flex flex-col sm:flex-row sm:items-center gap-3">
 <button
 type="button"
 onClick={openMenuManager}
 data-testid="setup-open-menu-manager-btn"
 className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-white text-black hover:bg-white/90 font-bold text-[13.5px] transition-all"
 >
 <UtensilsCrossed className="w-4 h-4" />
 {sections?.menu?.ok && sections?.categories?.ok? 'Open Menu Manager': 'Build your menu'}
 <ArrowRight className="w-3.5 h-3.5" />
 </button>
 <div className="flex flex-wrap items-center gap-2 text-[11.5px] text-white/55">
 {(sections?.categories?.count || 0) > 0 && (
 <span
 data-testid="setup-menu-summary-categories"
 className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-yellow-500/[0.06] border border-yellow-500/25 text-yellow-200 font-semibold"
 >
 <Layers className="w-3 h-3" />
 {sections.categories.count} {sections.categories.count === 1? 'category': 'categories'}
 </span>
 )}
 {(sections?.menu?.count || 0) > 0 && (
 <span
 data-testid="setup-menu-summary-items"
 className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-yellow-500/[0.06] border border-yellow-500/25 text-yellow-200 font-semibold"
 >
 <UtensilsCrossed className="w-3 h-3" />
 {sections.menu.count} {sections.menu.count === 1? 'item': 'items'}
 </span>
 )}
 {!sections?.menu?.ok ||!sections?.categories?.ok? (
 <span className="text-amber-300/85 font-semibold">
 Menu items missing — open Menu Manager to add them
 </span>
 ): null}
 </div>
 </div>
 </SectionShell>

 {/* Section — Order Receiving Channels */}
 <SectionShell
 idx={5}
 title="Order receiving channels"
 subtitle={
 sections?.order_channels?.ok
? 'At least one channel is enabled — you can receive orders.'
: 'Choose how customers reach you and how you receive new orders. At least one channel is required.'
 }
 ok={!!sections?.order_channels?.ok}
 icon={MessageCircle}
 testid="setup-section-order-channels"
 >
 <OrderChannelsForm
 restaurantId={restaurant?.id}
 token={token}
 compact
 onChange={() => refreshReadiness()}
 />
 </SectionShell>

 {/* Section — Order Fulfillment Settings (Pickup & Delivery) */}
 <SectionShell
 idx={6}
 title="Order fulfillment settings"
 subtitle={
 sections?.fulfillment?.ok
? 'At least one fulfillment method is enabled — customers can place orders.'
: 'Configure pickup and delivery times so customers see accurate ETAs at checkout. At least one method is required.'
 }
 ok={!!sections?.fulfillment?.ok}
 icon={Clock}
 testid="setup-section-fulfillment"
 >
 <FastByNoFeeMarketing variant="inline-hint" className="mb-4" testid="onboarding-fast-hint" />
 <OrderFulfillmentForm
 restaurantId={restaurant?.id}
 token={token}
 onChange={() => refreshReadiness()}
 />
 </SectionShell>

 {/* Section — Payments verification */}
 <SectionShell
 idx={7}
 title="Payments verification"
 subtitle="Your payouts are connected. Stripe sends order proceeds straight to your bank."
 ok={!!sections?.payouts?.ok}
 icon={CreditCard}
 testid="setup-section-payments"
 >
 <div
 data-testid="setup-payments-status"
 data-ok={sections?.payouts?.ok? 'true': 'false'}
 className={`flex items-start gap-3 p-4 rounded-2xl border ${
 sections?.payouts?.ok
? 'border-emerald-500/30 bg-emerald-500/[0.05]'
: 'border-amber-500/30 bg-amber-500/[0.05]'
 }`}
 >
 <span className={`mt-0.5 w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${
 sections?.payouts?.ok
? 'bg-emerald-500/15 border border-emerald-500/35'
: 'bg-amber-500/15 border border-amber-500/35'
 }`}>
 {sections?.payouts?.ok? (
 <Check className="w-3.5 h-3.5 text-emerald-300" strokeWidth={3} />
 ): (
 <AlertCircle className="w-3.5 h-3.5 text-amber-300" />
 )}
 </span>
 <div className="min-w-0">
 <p className={`text-[13.5px] font-bold ${sections?.payouts?.ok? 'text-emerald-200': 'text-amber-200'}`}>
 {sections?.payouts?.ok? 'Stripe Connected · Payouts enabled': 'Stripe verification pending'}
 </p>
 <p className="mt-1 text-[12.5px] text-white/60 leading-relaxed">
 {sections?.payouts?.ok
? 'Payments go directly to your own Stripe account. NoFee never holds your money — payouts follow your Stripe payout schedule, which you can review anytime from your Stripe dashboard.'
: "Stripe is still finalizing verification. You can continue setting up your NoFee account while verification completes."}
 </p>
 </div>
 </div>
 <div className="pt-4">
 <button
 type="button"
 onClick={() => navigate('/onboarding/stripe-connect')}
 data-testid="setup-payments-manage-btn"
 className="inline-flex items-center gap-2 px-5 py-3 rounded-xl border border-white/15 hover:border-white/30 text-white/85 hover:text-white font-bold text-[13.5px] transition-all"
 >
 {sections?.payouts?.ok? 'Manage Stripe': 'Connect Stripe'}
 <ArrowRight className="w-3.5 h-3.5" />
 </button>
 </div>
 </SectionShell>

 {/* Section — Preview menu */}
 <SectionShell
 idx={8}
 title="Preview your menu"
 subtitle="See exactly what customers will see before you publish."
 ok={!!sections?.menu?.ok &&!!sections?.profile?.ok}
 icon={Eye}
 testid="setup-section-preview"
 >
 <div className="flex flex-col sm:flex-row sm:items-center gap-3">
 <a
 href={restaurant?.slug? `/menu/${restaurant.slug}?preview=1`: '#'}
 target="_blank"
 rel="noreferrer"
 data-testid="setup-preview-link"
 className={`inline-flex items-center gap-2 px-5 py-3 rounded-xl font-bold text-[13.5px] transition-all ${
 restaurant?.slug
? 'bg-white text-black hover:bg-white/90'
: 'bg-white/10 text-white/40 cursor-not-allowed'
 }`}
 onClick={(e) => {
 if (!restaurant?.slug) e.preventDefault();
 }}
 >
 <Eye className="w-4 h-4" />
 Preview my menu
 <ExternalLink className="w-3.5 h-3.5" />
 </a>
 <p className="text-[11.5px] text-white/45">
 {restaurant?.slug
? "Opens your public menu in a new tab. It's not indexed by search until you publish."
: 'Complete your restaurant profile first to enable the preview.'}
 </p>
 </div>
 </SectionShell>
 </div>

 {/* Step 9 — Publish */}
 <div
 data-testid="setup-section-publish"
 className="mt-10 p-6 sm:p-8 rounded-3xl border border-yellow-500/30 bg-gradient-to-br from-yellow-500/[0.06] to-amber-500/[0.02]"
 >
 <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-5">
 <div className="flex-1">
 <div className="inline-flex items-center gap-2 mb-2 px-2.5 py-0.5 rounded-full bg-yellow-500/15 border border-yellow-500/35 text-yellow-300 text-[10.5px] uppercase tracking-[0.18em] font-bold">
 <Rocket className="w-3 h-3" /> Step 9 · Publish
 </div>
 <h3 className="font-['Outfit',_Inter,_sans-serif] text-[22px] sm:text-[26px] font-medium tracking-tight text-white">
 Ready when you are.
 </h3>
 <p
 data-testid="setup-publish-summary"
 className="mt-2 text-[13.5px] text-white/60 max-w-md leading-relaxed"
 >
 {ready
? 'Everything looks good. Publish your restaurant to start receiving orders.'
: `${completedCount} of ${checklist.length} requirements complete — ${missingItems.length} remaining.`}
 </p>
 </div>
 <button
 type="button"
 onClick={onGoLive}
 disabled={goingLive}
 data-testid="setup-go-live-btn"
 className={`group shrink-0 inline-flex items-center justify-center gap-2 px-7 py-4 min-h-[56px] rounded-2xl text-black font-extrabold text-[15px] tracking-tight transition-all hover:-translate-y-0.5 disabled:translate-y-0 disabled:cursor-not-allowed ${
 ready
? 'bg-gradient-to-b from-yellow-300 to-yellow-500 hover:from-yellow-200 hover:to-yellow-400 shadow-[0_18px_50px_-12px_rgba(250,204,21,0.55)]'
: 'bg-white/[0.08] border border-white/15 text-white/70 hover:bg-white/[0.12] hover:text-white/90 shadow-none'
 }`}
 >
 {goingLive? (
 <Loader2 className="w-4 h-4 animate-spin" />
 ): (
 <Rocket className="w-4 h-4" />
 )}
 {goingLive? 'Publishing…': 'Publish My Restaurant'}
 {!goingLive && (
 <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
 )}
 </button>
 </div>

 {/* Granular per-field checklist — exact items blocking publication.
 Each MISSING item is rendered as an interactive button that scrolls
 to and highlights the corresponding field. Completed items stay
 as static text. */}
 <div
 data-testid="setup-publish-checklist"
 className="mt-6 pt-6 border-t border-yellow-500/15 grid sm:grid-cols-2 gap-x-6 gap-y-2.5"
 >
 {checklist.map((c) => {
 const Wrapper = c.ok? 'div': 'button';
 const wrapperProps = c.ok
? {}
: {
 type: 'button',
 onClick: () => goToChecklistItem(c.key),
 'aria-label': `Go to ${c.label}`,
 };
 return (
 <Wrapper
 key={c.key}
 data-testid={`publish-check-${c.key}`}
 data-ok={c.ok? 'true': 'false'}
 {...wrapperProps}
 className={`flex items-start gap-2.5 text-[13px] text-left w-full rounded-lg px-2 -mx-2 py-1 transition-colors ${
 c.ok? '': 'hover:bg-rose-500/[0.07] cursor-pointer'
 }`}
 >
 <span
 className={`mt-0.5 w-4 h-4 shrink-0 rounded-full flex items-center justify-center ${
 c.ok
? 'bg-emerald-500/15 ring-1 ring-emerald-500/40'
: 'bg-rose-500/15 ring-1 ring-rose-500/45'
 }`}
 aria-hidden="true"
 >
 {c.ok? (
 <Check className="w-2.5 h-2.5 text-emerald-300" strokeWidth={3.5} />
 ): (
 <AlertCircle className="w-2.5 h-2.5 text-rose-300" strokeWidth={3} />
 )}
 </span>
 <span className={c.ok? 'text-white/70 line-through decoration-emerald-500/40 decoration-1': 'text-white/90 font-medium'}>
 {c.label}
 {!c.ok && (
 <span className="ml-1.5 inline-flex items-center gap-0.5 text-[10.5px] uppercase tracking-[0.16em] text-rose-300/80 font-bold">
 required <ArrowRight className="w-2.5 h-2.5 opacity-70" strokeWidth={2.5} />
 </span>
 )}
 </span>
 </Wrapper>
 );
 })}
 </div>

 {/* Actionable "Remaining Tasks" panel — each row is a Go-to button
 that scrolls + focuses + highlights the exact field. Replaces
 the previous passive red banner. */}
 {missingItems.length > 0 && (
 <div
 data-testid="setup-publish-remaining-tasks"
 className="mt-5 p-4 sm:p-5 rounded-2xl border border-rose-500/25 bg-rose-500/[0.04]"
 >
 <div className="flex items-center gap-2 mb-3">
 <span className="text-[10.5px] uppercase tracking-[0.18em] text-rose-300 font-bold">
 Remaining Tasks · {missingItems.length}
 </span>
 </div>
 <ul className="space-y-2">
 {missingItems.map((m) => {
 const t = CHECKLIST_TARGETS[m.key];
 return (
 <li key={m.key} className="flex items-center justify-between gap-3">
 <span className="flex items-center gap-2 min-w-0 text-[13.5px] text-white/85">
 <span className="w-1.5 h-1.5 rounded-full bg-rose-400 shrink-0" aria-hidden="true" />
 <span className="truncate">{m.label}</span>
 </span>
 <button
 type="button"
 onClick={() => goToChecklistItem(m.key)}
 data-testid={`go-to-${m.key}`}
 className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-rose-500/15 hover:bg-rose-500/25 border border-rose-500/30 text-rose-100 text-[12px] font-bold tracking-tight transition-colors"
 >
 {m.key === 'items' || m.key === 'categories'
? 'Open Menu Manager'
: `Go to ${t?.label || m.label}`}
 <ArrowRight className="w-3 h-3" />
 </button>
 </li>
 );
 })}
 </ul>
 </div>
 )}
 </div>

 <div className="mt-8 text-center">
 <button
 type="button"
 onClick={() => navigate('/dashboard')}
 data-testid="setup-skip-link"
 className="text-[12.5px] text-white/45 hover:text-white/75 underline underline-offset-2"
 >
 Skip for now — I'll finish later
 </button>
 </div>

          {/* "Almost Ready!" modal removed in iter132 — owners reported
              the extra modal felt unrelated. The Publish My Restaurant
              click now jumps DIRECTLY to the first missing field with
              a gold-pulse highlight + a sonner toast, so the owner
              knows exactly what to fix without an extra full-screen
              modal getting in the way. */}

 {/* Highlight keyframes for the temporary gold pulse used when the
 owner clicks a missing checklist item. Defined once here so we
 don't need to touch the global Tailwind config. */}
 <style>{`
 @keyframes nfFocusPulse {
 0% { box-shadow: 0 0 0 0 rgba(250,204,21,0.55); outline-color: rgba(250,204,21,0.85); }
 70% { box-shadow: 0 0 0 12px rgba(250,204,21,0); outline-color: rgba(250,204,21,0.55); }
 100% { box-shadow: 0 0 0 0 rgba(250,204,21,0); outline-color: rgba(250,204,21,0.0); }
 }
.nf-focus-highlight {
 outline: 2px solid rgba(250,204,21,0.85)!important;
 outline-offset: 3px;
 border-radius: 12px;
 animation: nfFocusPulse 1.2s ease-out 2;
 }
 /* Green pulse painted on the checklist row that just flipped to
 ok — fires when the auto-return effect scrolls back to the
 checklist after a fix. Pairs with pendingChecklistKey state. */
 @keyframes nfChecklistDone {
 0% { background-color: rgba(16,185,129,0.18); box-shadow: 0 0 0 0 rgba(16,185,129,0.55); }
 70% { background-color: rgba(16,185,129,0.08); box-shadow: 0 0 0 8px rgba(16,185,129,0); }
 100% { background-color: transparent; box-shadow: 0 0 0 0 rgba(16,185,129,0); }
 }
.nf-checklist-just-done {
 animation: nfChecklistDone 2.2s ease-out 1;
 border-radius: 8px;
 }
 `}</style>
 </fieldset>
 </div>
 <ImageCropperModal
 open={!!cropperSrc}
 src={cropperSrc}
 aspect={CROP_ASPECT[cropperTarget] || 4 / 3}
 title={CROP_TITLE[cropperTarget] || 'Adjust photo'}
 onSave={onCropperSave}
 onCancel={closeCropper}
 />

 {/* iter142 — Menu Manager modal. Renders the SAME <Dashboard />
     the owner sees post-onboarding, but with `embedded={true}` so
     the sidebar / hero / bell / welcome flow are hidden. Same
     component, same endpoints, same MongoDB collections — zero
     duplication. The embedded header's "Save & Return to
     Onboarding" button calls `closeMenuManager()` which unmounts
     the modal and refreshes readiness so the checklist rows flip
     to ✓ automatically. */}
 {menuManagerOpen && createPortal(
   <div
     data-testid="onboarding-menu-manager-modal"
     className="fixed inset-0 z-[70] bg-black overflow-y-auto"
   >
     <Dashboard embedded onEmbeddedClose={closeMenuManager} />
   </div>,
   document.body,
 )}
 </OnboardingShell>
 );
};

export default OnboardingSetupPage;

// ─────────────────────────────────────────────────────────────────────
// GoLiveGrowPopup — celebrates Go Live + invites the owner to request
// their first marketing campaign via /dashboard/grow.
// Rendered ONLY inside the success state of the Go Live flow.
// ─────────────────────────────────────────────────────────────────────
const GoLiveGrowPopup = () => {
 const navigate = useNavigate();
 const [open, setOpen] = useState(true);
 if (!open) return null;
 return (
 <div
 data-testid="go-live-grow-popup"
 className="fixed inset-0 z-[10001] flex items-center justify-center p-4"
 role="dialog"
 aria-modal="true"
 aria-labelledby="go-live-grow-title"
 >
 <div className="absolute inset-0 bg-black/85 backdrop-blur-sm" onClick={() => setOpen(false)} />
 <div className="relative w-full max-w-md rounded-3xl border border-fuchsia-500/30 bg-gradient-to-br from-zinc-950 via-black to-zinc-950 shadow-2xl p-7 sm:p-9 text-center">
 <button
 onClick={() => setOpen(false)}
 data-testid="go-live-grow-popup-dismiss"
 className="absolute top-3 right-3 w-9 h-9 rounded-full text-zinc-400 hover:text-white hover:bg-white/5 flex items-center justify-center transition-colors"
 aria-label="Dismiss"
 >
 <X className="w-4 h-4" />
 </button>
 <div className="inline-flex w-16 h-16 rounded-2xl bg-gradient-to-br from-fuchsia-500/30 to-amber-500/30 border border-fuchsia-500/40 items-center justify-center mb-5">
 <span className="text-3xl">🎉</span>
 </div>
 <h2 id="go-live-grow-title" className="text-2xl sm:text-[26px] font-extrabold text-white tracking-tight leading-tight">
 Congratulations!
 </h2>
 <p className="mt-3 text-sm text-white/80 leading-relaxed">
 Your restaurant is now <span className="text-gold-300 font-semibold">live</span>.
 </p>
 <p className="mt-4 text-sm text-white/75 leading-relaxed">
 Ready to attract more direct customers? NoFee can help create personalized marketing campaigns for your restaurant.
 </p>
 <button
 onClick={() => navigate('/dashboard/grow')}
 data-testid="go-live-grow-popup-cta"
 className="mt-7 inline-flex items-center justify-center gap-2 w-full px-5 py-3.5 rounded-xl bg-gold-500 text-black font-extrabold text-[14px] hover:bg-gold-400 transition-colors"
 >
 <Rocket className="w-4 h-4" /> Grow My Restaurant
 </button>
 <button
 onClick={() => setOpen(false)}
 className="mt-3 text-[12px] text-zinc-400 hover:text-white transition-colors"
 >
 Maybe later
 </button>
 </div>
 </div>
 );
};
